
function compareTableValue(a, b, type = "text") {
  if (type === "number") {
    return (Number(a || 0) - Number(b || 0));
  }
  if (type === "date") {
    return (new Date(a || 0).getTime() || 0) -
           (new Date(b || 0).getTime() || 0);
  }
  return String(a ?? "").localeCompare(String(b ?? ""), "id", {
    numeric: true, sensitivity: "base"
  });
}

function sortTableRows(rows, config) {
  const { key, direction, type = "text" } = config;
  const sign = direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const result = compareTableValue(a?.[key], b?.[key], type);
    return result === 0 ? 0 : result * sign;
  });
}

function SortHead({ label, field, config, onSort, type = "text", className = "" }) {
  const active = config.key === field;
  return (
    <button type="button" onClick={() => onSort(field, type)}
      className={`inline-flex items-center gap-1 font-semibold hover:text-orange-600 ${className}`}
      title={`Urutkan ${label}`}>
      {label}
      <span aria-hidden="true" className={active ? "text-orange-600" : "text-slate-400"}>
        {active ? (config.direction === "asc" ? "↑" : "↓") : "↕"}
      </span>
    </button>
  );
}

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useRealtime } from "@/lib/ws";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Receipt } from "@/components/Receipt";
import { Printer } from "lucide-react";

const rupiah = (v) => "Rp" + (Math.round(v || 0)).toLocaleString("id-ID");

export default function TransactionsPage() {
  const [txs, setTxs] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [outletId, setOutletId] = useState("all");
  const [reprintTx, setReprintTx] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: "created_at", direction: "desc", type: "date" });
  const toggleSort = (key, type = "text") => setSortConfig(prev => ({
    key,
    type,
    direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc"
  }));

  const load = () => {
    const url = outletId === "all" ? "/transactions?limit=200" : `/transactions?outlet_id=${outletId}&limit=200`;
    api.get(url).then(({data})=>setTxs(data));
  };
  useEffect(() => {
    api.get("/outlets").then(({data})=>setOutlets(data));
  }, []);
  useEffect(() => { load(); }, [outletId]); // eslint-disable-line

  useRealtime((msg) => {
    if (["transaction.created", "transaction.paid"].includes(msg.event)) load();
  });

  const outletName = (id) => outlets.find(o=>o.id===id)?.name || id;

  return (
    <div className="page-shell">
      <div>
        <div data-workspace-redundant-header><h1 className="text-2xl font-extrabold font-display">Transaksi</h1></div>
        <p className="text-slate-500 text-sm">{txs.length} transaksi terakhir</p>
      </div>
      <Card>
        <CardHeader className="flex-row items-center gap-3">
          <CardTitle className="text-base flex-1">Riwayat</CardTitle>
          <Select value={outletId} onValueChange={setOutletId}>
            <SelectTrigger data-testid="transactions-outlet-filter" className="w-56"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua Outlet</SelectItem>
              {outlets.map(o=><SelectItem key={o.id} value={o.id}>{o.brand}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead><SortHead label="No. Transaksi" field="trx_no" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Waktu" field="created_at" type="date" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Outlet" field="outlet_id" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Kasir" field="cashier_name" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Item" field="items" type="number" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Total" field="total" type="number" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Profit" field="gross_profit" type="number" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Metode" field="payment_method" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead><SortHead label="Status" field="status" type="text" config={sortConfig} onSort={toggleSort} /></TableHead>
                <TableHead className="text-right w-20">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortTableRows(txs, sortConfig).map(t => (
                <TableRow key={t.id}>
                  <TableCell className="font-mono text-xs">{t.trx_no}</TableCell>
                  <TableCell className="text-xs text-slate-600">{new Date(t.created_at).toLocaleString("id-ID")}</TableCell>
                  <TableCell className="text-sm">{outletName(t.outlet_id)}</TableCell>
                  <TableCell className="text-sm">{t.cashier_name}</TableCell>
                  <TableCell className="text-right">{t.items.length}</TableCell>
                  <TableCell className="text-right font-semibold num">{rupiah(t.total)}</TableCell>
                  <TableCell className="text-right num text-emerald-600">{rupiah(t.gross_profit)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{t.payment_method?.toUpperCase()}</Badge></TableCell>
                  <TableCell><Badge className={t.status==="paid" ? "bg-emerald-500":"bg-amber-500"}>{t.status?.toUpperCase()}</Badge></TableCell>
                  <TableCell className="text-right">
                    <Button data-testid={`reprint-${t.id}`} size="sm" variant="outline" onClick={()=>setReprintTx(t)} className="h-7 px-2"><Printer className="w-3.5 h-3.5"/></Button>
                  </TableCell>
                </TableRow>
              ))}
              {!txs.length && <TableRow><TableCell colSpan={10} className="text-center py-8 text-slate-400">Belum ada transaksi</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Receipt tx={reprintTx} outlet={outlets.find(o=>o.id===reprintTx?.outlet_id)} onClose={()=>setReprintTx(null)} autoOpen={false}/>
    </div>
  );
}
